// Uren & inkoop op de projectpagina (wiki b30c9aec, akkoord Joris 22 sep).
//
// Uren: geschreven (Gripp-urenspiegel) naast begroot (offerteregels), met de uren per
// persoon — de doorklik vanuit de urenkolom in het projectenoverzicht landt hier.
// Inkoop: prijsafspraken met freelancers/leveranciers per regel; de som is de inkoop in
// de Revenue Forecast. Tarieven en bedragen ziet alleen wie de forecast of sales mag
// zien — de server laat ze anders weg. Rustig: één kleur voor de balk, verder grijs.
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, TextField, MenuItem, Autocomplete, Button } from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { biedOngedaanAan } from '../../../utils/ongedaan';
import { T, Icon, StitchIconBtn, stitchSkeleton } from '../parts/stitch';
import { UrenBalk } from '../list/onderdelen';
import { formatUren, urenFractie } from '../list/projectGezondheid';
import {
  AFSPRAKEN, berekenBedrag, formatEuro, afspraakTekst, leegFormulier, formulierUitRegel, naarBody, formulierFout,
} from '../parts/inkoop';

const kopSx = { fontFamily: T.font, fontWeight: 700, fontSize: '0.95rem', color: T.text, mb: 1 };
const tekstSx = { fontFamily: T.fontBody, fontSize: '0.85rem', color: T.text };
const stilSx = { fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outline };
const veldSx = { '& .MuiInputBase-root': { fontFamily: T.fontBody, fontSize: '0.85rem' }, '& label': { fontFamily: T.fontBody, fontSize: '0.8rem' } };

const datumKort = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

async function leesFout(r) {
  try { const d = await r.json(); return d?.detail || `HTTP ${r.status}`; } catch { return `HTTP ${r.status}`; }
}

// ── Uren ─────────────────────────────────────────────────────────────────
export function UrenBlok({ uren, laden }) {
  if (laden) return <Box sx={stitchSkeleton('100%', 60)} />;
  if (!uren) return <Typography sx={stilSx}>Uren konden niet worden opgehaald.</Typography>;
  if (!uren.gekoppeld) {
    return (
      <Typography sx={stilSx}>
        Nog niet gekoppeld aan een Gripp-project{uren.nummer ? ` (projectnummer ${uren.nummer} niet gevonden)` : ''} — de uren verschijnen zodra het project in Gripp staat.
      </Typography>
    );
  }
  const heeftBudget = uren.begroot != null;
  const fractie = heeftBudget ? urenFractie(uren.begroot, uren.geschreven) : null;
  const pctTekst = Number.isFinite(fractie) ? `${Math.round(fractie * 100)}%` : 'boven budget';
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
        <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '1.15rem', color: T.text }}>
          {formatUren(uren.geschreven)} u
        </Typography>
        <Typography sx={stilSx}>
          {heeftBudget ? `geschreven van ${formatUren(uren.begroot)} u begroot · ${pctTekst}` : 'geschreven — geen urenregels in de offerte'}
        </Typography>
      </Box>
      {heeftBudget ? <UrenBalk fractie={fractie} hoogte={6} /> : null}
      <Typography sx={{ ...stilSx, mt: 0.75 }}>
        Uit Gripp{uren.bijgewerkt ? `, bijgewerkt ${datumKort(uren.bijgewerkt)}` : ''}.
        {uren.metDagen ? ' Dagregels tellen als 8 uur.' : ''}
        {uren.handmatig ? ` Handmatig budget${uren.handmatigeSoorten?.length ? ` voor ${uren.handmatigeSoorten.join(', ').toLowerCase()}` : ''} (Declarabiliteit) vervangt de offerteregels.` : ''}
      </Typography>

      {uren.perPersoon?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ ...stilSx, fontWeight: 600, mb: 0.5 }}>Per persoon</Typography>
          {uren.perPersoon.map((p) => (
            <Box key={`${p.naam}-${p.laatste}`} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 72px 110px', gap: 1.5,
              py: 0.6, borderBottom: '1px solid rgba(45,51,53,0.06)', alignItems: 'baseline' }}>
              <Typography noWrap sx={tekstSx}>{p.naam}</Typography>
              <Typography sx={{ ...tekstSx, textAlign: 'right' }}>{formatUren(p.uren)} u</Typography>
              <Typography noWrap sx={{ ...stilSx, textAlign: 'right' }}>{p.laatste ? `laatst ${datumKort(p.laatste)}` : ''}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── Inkoop ───────────────────────────────────────────────────────────────
function InkoopFormulier({ form, setForm, magBedragen, opties, bezig, bewerken, onOpslaan, onAnnuleer }) {
  const fout = formulierFout(form, magBedragen);
  const zet = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <Box component="form" onSubmit={(e) => { e.preventDefault(); if (!fout) onOpslaan(); }}
      sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: 'minmax(180px,1.4fr) 130px 110px 90px minmax(140px,1fr)' }, alignItems: 'start', mt: 1.5 }}>
      <Autocomplete freeSolo size="small" options={opties} getOptionLabel={(o) => (typeof o === 'string' ? o : o.naam)}
        inputValue={form.leverancier}
        onInputChange={(_, v, reden) => { if (reden !== 'reset') setForm((f) => ({ ...f, leverancier: v, leverancier_ref: null })); }}
        onChange={(_, v) => setForm((f) => ({ ...f, leverancier: typeof v === 'string' ? v : (v?.naam || ''), leverancier_ref: typeof v === 'string' ? null : (v?.id || null) }))}
        renderInput={(params) => <TextField {...params} label="Freelancer / leverancier" sx={veldSx} />} />
      <TextField select size="small" label="Afspraak" value={form.afspraak} onChange={zet('afspraak')} sx={veldSx}>
        {AFSPRAKEN.map((a) => <MenuItem key={a.value} value={a.value} sx={{ fontSize: '0.85rem' }}>{a.label}</MenuItem>)}
      </TextField>
      {magBedragen
        ? <TextField size="small" label={form.afspraak === 'vast' ? 'Bedrag' : 'Tarief'} value={form.tarief} onChange={zet('tarief')} inputProps={{ inputMode: 'decimal' }} sx={veldSx} />
        : <Box sx={{ display: { xs: 'none', md: 'block' } }} />}
      <TextField size="small" label="Aantal" value={form.aantal} onChange={zet('aantal')} inputProps={{ inputMode: 'decimal' }} sx={veldSx} />
      <TextField size="small" label="Omschrijving" value={form.omschrijving} onChange={zet('omschrijving')} sx={veldSx} />
      <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button type="submit" size="small" variant="contained" disableElevation disabled={!!fout || bezig}
          sx={{ textTransform: 'none', fontFamily: T.fontBody, bgcolor: T.primary, '&:hover': { bgcolor: T.primaryDim } }}>
          {bewerken ? 'Opslaan' : 'Toevoegen'}
        </Button>
        {bewerken && <Button size="small" onClick={onAnnuleer} sx={{ textTransform: 'none', fontFamily: T.fontBody, color: T.secondary }}>Annuleren</Button>}
        {magBedragen && form.tarief !== '' && !fout && (
          <Typography sx={stilSx}>= {formatEuro(berekenBedrag(form.tarief, form.aantal))}</Typography>
        )}
        {!magBedragen && <Typography sx={stilSx}>Het tarief vult de projectleider in.</Typography>}
      </Box>
    </Box>
  );
}

export function InkoopBlok({ projectId }) {
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);
  const [form, setForm] = useState(leegFormulier);
  const [bewerkId, setBewerkId] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [opties, setOpties] = useState([]);

  const laad = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/projects/${projectId}/inkoop`);
      if (!r.ok) throw new Error(await leesFout(r));
      setData(await r.json());
      setFout(null);
    } catch (e) { setFout(e.message || String(e)); }
  }, [projectId]);

  useEffect(() => { if (projectId) laad(); }, [projectId, laad]);

  useEffect(() => {
    let actueel = true;
    authFetch(`${BACKEND_URL}/Api/personen/crew`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!actueel || !d) return;
        const namen = (d.records || []).map((p) => ({ id: p.id, naam: p.fields?.['Volledige naam'] || p.fields?.Naam || '' }))
          .filter((p) => p.naam);
        setOpties(namen);
      })
      .catch(() => {});
    return () => { actueel = false; };
  }, []);

  const verwerk = async (r) => {
    if (!r.ok) throw new Error(await leesFout(r));
    const d = await r.json();
    setData(d);
    if (d.melding) toast(d.melding, { duration: 8000, icon: 'ℹ️' });
    return d;
  };

  const opslaan = async () => {
    const magBedragen = !!data?.magBedragen;
    setBezig(true);
    try {
      const url = bewerkId ? `${BACKEND_URL}/Api/projects/${projectId}/inkoop/${bewerkId}` : `${BACKEND_URL}/Api/projects/${projectId}/inkoop`;
      await verwerk(await authFetch(url, {
        method: bewerkId ? 'PATCH' : 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(naarBody(form, magBedragen)),
      }));
      setForm(leegFormulier());
      setBewerkId(null);
    } catch (e) { toast.error(`Inkoop niet opgeslagen: ${e.message || e}`); }
    finally { setBezig(false); }
  };

  const verwijder = async (regel) => {
    try {
      await verwerk(await authFetch(`${BACKEND_URL}/Api/projects/${projectId}/inkoop/${regel.id}`, { method: 'DELETE' }));
      if (bewerkId === regel.id) { setBewerkId(null); setForm(leegFormulier()); }
      biedOngedaanAan(`Inkoop ${regel.leverancier} verwijderd`, async () => {
        await verwerk(await authFetch(`${BACKEND_URL}/Api/projects/${projectId}/inkoop/${regel.id}/herstel`, { method: 'POST' }));
      });
    } catch (e) { toast.error(`Verwijderen mislukt: ${e.message || e}`); }
  };

  if (fout && !data) return <Typography sx={stilSx}>Inkoop kon niet worden geladen: {fout}</Typography>;
  if (!data) return <Box sx={stitchSkeleton('100%', 60)} />;

  const { regels = [], magBedragen, totaal, forecast } = data;
  return (
    <Box>
      {regels.length === 0 && (
        <Typography sx={stilSx}>Nog geen inkoop vastgelegd. Zet hier de prijsafspraken met freelancers en leveranciers neer.</Typography>
      )}
      {regels.map((r) => (
        <Box key={r.id} sx={{ display: 'grid', gridTemplateColumns: magBedragen ? 'minmax(0,1fr) auto 96px 64px' : 'minmax(0,1fr) auto 64px',
          gap: 1.5, alignItems: 'center', py: 0.75, borderBottom: '1px solid rgba(45,51,53,0.06)',
          bgcolor: bewerkId === r.id ? T.surfaceLow : 'transparent' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={tekstSx}>{r.leverancier}</Typography>
            {r.omschrijving && <Typography noWrap sx={stilSx}>{r.omschrijving}</Typography>}
          </Box>
          <Typography sx={{ ...stilSx, whiteSpace: 'nowrap' }}>{afspraakTekst(r, magBedragen)}</Typography>
          {magBedragen && <Typography sx={{ ...tekstSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatEuro(r.bedrag)}</Typography>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
            <StitchIconBtn title="Wijzigen" role="button" aria-label="Wijzigen" onClick={() => { setBewerkId(r.id); setForm(formulierUitRegel(r)); }}>
              <Icon name="edit" size={16} />
            </StitchIconBtn>
            <StitchIconBtn title="Verwijderen" role="button" aria-label="Verwijderen" onClick={() => verwijder(r)}>
              <Icon name="delete" size={16} />
            </StitchIconBtn>
          </Box>
        </Box>
      ))}
      {magBedragen && regels.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', pt: 1, gap: 2, flexWrap: 'wrap' }}>
          <Typography sx={stilSx}>Deze som is de inkoop in de Revenue Forecast.</Typography>
          <Typography sx={{ ...tekstSx, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>Totaal {formatEuro(totaal)}</Typography>
        </Box>
      )}
      {magBedragen && regels.length === 0 && forecast?.gevonden && forecast.inkoop > 0 && (
        <Typography sx={{ ...stilSx, mt: 1 }}>
          In de Revenue Forecast staat nu {formatEuro(forecast.inkoop)} handmatig als inkoop. Zodra je hier een regel toevoegt, telt de som van de regels.
        </Typography>
      )}
      <InkoopFormulier form={form} setForm={setForm} magBedragen={!!magBedragen} opties={opties} bezig={bezig}
        bewerken={!!bewerkId} onOpslaan={opslaan} onAnnuleer={() => { setBewerkId(null); setForm(leegFormulier()); }} />
    </Box>
  );
}

export default function UrenInkoopPanel({ projectId, uren, urenLaden }) {
  return (
    <Box sx={{ display: 'grid', gap: 3.5, maxWidth: 860 }}>
      <Box>
        <Typography sx={kopSx}>Uren</Typography>
        <UrenBlok uren={uren} laden={urenLaden} />
      </Box>
      <Box>
        <Typography sx={kopSx}>Inkoop</Typography>
        <InkoopBlok projectId={projectId} />
      </Box>
    </Box>
  );
}
